import { ArrowLeftOutlined, DeleteOutlined, SaveOutlined } from '@ant-design/icons';
import { App, AutoComplete, Button, Col, DatePicker, Form, Input, InputNumber, Row, Select, Space, Spin, message } from 'antd';
import { useContext, useEffect, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link, useNavigate, useParams } from 'react-router-dom';
import phytosanitaryProductApi from '../../../apis/PhytosanitaryProductApi';
import plotApi from '../../../apis/PlotApi';
import treatmentApi from '../../../apis/TreatmentApi';
import ConstantLabel from '../../../components/ConstantLabel/ConstantLabel';
import LayoutComponent from '../../../components/LayoutComponent/LayoutComponent';
import PhytosanitaryProductComponent from '../../../components/PhytosanitaryProductComponent/PhytosanitaryProductComponent';
import CustomContext from '../../../contexts/CustomContext';
import { CustomAuth, PhytosanitaryProduct, Plot, Treatment } from '../../../models/Entities';
import { crops, doseUnits } from '../../../models/Types';
import alertService from '../../../services/AlertService';
import stringService from '../../../services/StringService';
import styles from './TreatmentPage.module.scss';

/**
 * Returns the treatment page to create or update a treatment.
 * @returns the treatment page.
 */
const TreatmentPage: React.FC = () => {
    /*** HOOKS ***/

    const intl = useIntl();
    const { modal } = App.useApp();
    const context = useContext(CustomContext);
    const auth = context.auth as CustomAuth;
    const companyId = auth.companyId as number;
    const [form] = Form.useForm();
    const navigate = useNavigate();
    const [loading, setLoading] = useState<'loading' | 'saving' | 'deleting'>();
    const [treatment, setTreatment] = useState<Treatment>();
    const [plots, setPlots] = useState<Plot[]>([]);
    const [productSuggestions, setProductSuggestions] = useState<PhytosanitaryProduct[]>([]);
    const [loadingSuggestions, setLoadingSuggestions] = useState<boolean>(false);
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout>>();
    const params = useParams<ParamsType>();
    const productRegisterNumber = Form.useWatch('productRegisterNumber', form);

    /*** EFFECTS ***/

    // list plots
    useEffect(() => {
        const init = async () => {
            try {
                const plotsPage = await plotApi.list(0, 1000, 'name', true, companyId);
                setPlots(plotsPage.content);
            } catch (error) {
                alertService.displayError(error, intl);
            }
        };
        init();
    }, [intl, companyId]);

    // load treatment
    useEffect(() => {
        const init = async () => {
            try {
                if (params.id !== 'new') {
                    setLoading('loading');
                    const treatment = await treatmentApi.get(+params.id!);
                    form.setFieldsValue(treatment);
                    setTreatment(treatment);
                }
            } catch (error) {
                alertService.displayError(error, intl);
            } finally {
                setLoading(undefined);
            }
        };
        init();
    }, [form, intl, params.id]);

    /*** METHODS ***/

    const searchProducts = (registerNumber?: string, name?: string) => {
        clearTimeout(debounceTimerRef.current);
        if (!registerNumber && !name) {
            setProductSuggestions([]);
            return;
        }
        debounceTimerRef.current = setTimeout(async () => {
            try {
                setLoadingSuggestions(true);
                const page = await phytosanitaryProductApi.list(0, 10, 'name', true, registerNumber, name);
                setProductSuggestions(page.content);
            } catch {
                setProductSuggestions([]);
            } finally {
                setLoadingSuggestions(false);
            }
        }, 300);
    };

    const onProductSelect = (_: string, option: { product: PhytosanitaryProduct }) => {
        form.setFieldsValue({
            productName: option.product.name,
            productRegisterNumber: option.product.registerNumber
        });
        setProductSuggestions([]);
    };

    const save = async (values: any) => {
        try {
            setLoading('saving');

            let updatedTreatment: Treatment = Object.assign({}, treatment, values);
            if (updatedTreatment.id) {
                updatedTreatment = await treatmentApi.update(updatedTreatment);
            } else {
                updatedTreatment = await treatmentApi.create(updatedTreatment);
            }
            setTreatment(updatedTreatment);

            message.success(intl.formatMessage({ id: 'status.saved' }));
        } catch (error) {
            alertService.displayError(error, intl);
        } finally {
            setLoading(undefined);
        }
    };

    const remove = async () => {
        modal.confirm({
            title: intl.formatMessage({ id: 'treatment.deleteModal.title' }),
            okButtonProps: { loading: loading === 'deleting', danger: true },
            onOk: async () => {
                try {
                    if (treatment && treatment.id) {
                        setLoading('deleting');
                        await treatmentApi.delete(treatment.id);
                        message.success(intl.formatMessage({ id: 'status.deleted' }));
                        navigate('/treatments');
                    }
                } catch (error) {
                    alertService.displayError(error, intl);
                } finally {
                    setLoading(undefined);
                }
            }
        });
    };

    /*** VISUAL ***/

    const plotOptions = plots.map((plot) => (
        <Select.Option key={plot.id} value={plot.id}>
            {plot.name}
        </Select.Option>
    ));

    const cropOptions = crops.map((crop) => (
        <Select.Option key={crop} value={crop}>
            {intl.formatMessage({ id: `crop.type.${crop}` })}
        </Select.Option>
    ));

    const doseUnitOptions = doseUnits.map((doseUnit) => (
        <Select.Option key={doseUnit} value={doseUnit}>
            <ConstantLabel value={doseUnit} prefix="treatment.doseUnit." />
        </Select.Option>
    ));

    const nameOptions = productSuggestions.map((p) => ({
        value: p.name,
        label: `${p.name} (${p.registerNumber})`,
        product: p
    }));

    const registerOptions = productSuggestions.map((p) => ({
        value: p.registerNumber,
        label: `${p.registerNumber} - ${p.name}`,
        product: p
    }));

    return (
        <LayoutComponent
            title={<FormattedMessage id="treatment.title" />}
            menu="treatments"
            path={[
                { path: '/treatments', name: <FormattedMessage id="treatments.title" /> },
                { path: `/treatments/${treatment && treatment.id ? treatment.id : params.id}`, name: treatment && treatment.id && treatment.productName }
            ]}
        >
            <Form form={form} onFinish={save} colon={false} layout="vertical">
                <Row gutter={[28, 0]}>
                    <Col span={24} lg={12}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.productName" />}
                            name="productName"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <AutoComplete
                                options={nameOptions}
                                onSearch={(value) => searchProducts(undefined, value || undefined)}
                                onSelect={onProductSelect}
                                notFoundContent={loadingSuggestions ? <Spin size="small" /> : null}
                            >
                                <Input size="large" maxLength={250} addonAfter={<PhytosanitaryProductComponent registerNumber={productRegisterNumber} />} />
                            </AutoComplete>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={4}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.productRegisterNumber" />}
                            name="productRegisterNumber"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <AutoComplete
                                size="large"
                                className={styles.input}
                                options={registerOptions}
                                onSearch={(value) => searchProducts(value || undefined, undefined)}
                                onSelect={onProductSelect}
                                notFoundContent={loadingSuggestions ? <Spin size="small" /> : null}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={4}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.startDate" />}
                            name="startDate"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <DatePicker className={styles.input} size="large" format="DD/MM/YYYY" />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={4}>
                        <Form.Item label={<FormattedMessage id="treatment.endDate" />} name="endDate">
                            <DatePicker className={styles.input} size="large" format="DD/MM/YYYY" />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={12}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.plot" />}
                            name="plotId"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="treatment.plot" />}
                                allowClear
                            >
                                {plotOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={8}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.parcel" />}
                            name="parcel"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Input size="large" maxLength={250} />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={4}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.area" />}
                            name="area"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber size="large" className={styles.input} min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={6}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.dose" />}
                            name="dose"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <InputNumber size="large" className={styles.input} min={0} />
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={6}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.doseUnit" />}
                            name="doseUnit"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select size="large" placeholder={<FormattedMessage id="treatment.doseUnit" />} allowClear>
                                {doseUnitOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={8}>
                        <Form.Item
                            label={<FormattedMessage id="treatment.crop" />}
                            name="crop"
                            rules={[{ required: true, message: <FormattedMessage id="status.mandatory" /> }]}
                        >
                            <Select
                                size="large"
                                showSearch
                                filterOption={stringService.filterOptions}
                                placeholder={<FormattedMessage id="treatment.crop" />}
                                allowClear
                            >
                                {cropOptions}
                            </Select>
                        </Form.Item>
                    </Col>
                    <Col span={24} lg={4}>
                        <Form.Item label={<FormattedMessage id="treatment.cropVariety" />} name="cropVariety">
                            <Input size="large" maxLength={250} />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item label={<FormattedMessage id="treatment.technician" />} name="technician">
                            <Input size="large" maxLength={1000} />
                        </Form.Item>
                    </Col>
                </Row>
                <Row gutter={[24, 24]} className="buttons">
                    <Col span={16}>
                        <Space>
                            <Link to="/treatments">
                                <Button size="large" icon={<ArrowLeftOutlined />}></Button>
                            </Link>
                            <Button
                                type="primary"
                                htmlType="submit"
                                size="large"
                                loading={loading === 'loading' || loading === 'saving'}
                                icon={<SaveOutlined />}
                            >
                                <FormattedMessage id="button.save" tagName="span" />
                            </Button>
                            {treatment && treatment.id && <Button danger size="large" onClick={remove} icon={<DeleteOutlined />}></Button>}
                        </Space>
                    </Col>
                </Row>
            </Form>
        </LayoutComponent>
    );
};

export default TreatmentPage;
type ParamsType = { id: string };
